import {
    useState, useEffect, useRef, useCallback,
} from 'react';
import { addSeconds, format } from 'date-fns';

type CountdownProps = {
    initialTime: number; // initial time in seconds
    onTimeUp?: () => void; // callback function when time runs out
    formatString?: string; // optional format string
};

function useCountdown({ initialTime, onTimeUp, formatString = 'mm:ss' }: CountdownProps) {
    const [timeLeft, setTimeLeft] = useState(initialTime);
    const intervalId = useRef<NodeJS.Timeout | null>(null);

    const startCountdown = useCallback(() => {
        if (intervalId.current) return; // Prevent multiple intervals

        intervalId.current = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 1) {
                    clearInterval(intervalId.current!);
                    intervalId.current = null;
                    onTimeUp?.();
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);
    }, [onTimeUp]);

    const stopCountdown = useCallback(() => {
        if (intervalId.current) {
            clearInterval(intervalId.current);
            intervalId.current = null;
        }
    }, []);

    const resetCountdown = useCallback(() => {
        setTimeLeft(initialTime);
        if (intervalId.current) {
            clearInterval(intervalId.current);
            intervalId.current = null;
        }
    }, [initialTime]);

    useEffect(() => {
        setTimeLeft(initialTime);
    }, [initialTime]);

    const formattedTime = format(addSeconds(new Date(0), timeLeft), formatString);

    return {
        timeLeft, formattedTime, startCountdown, stopCountdown, resetCountdown,
    };
}

export { useCountdown };
